/**
 * NECS Admin department metadata + Home administration + department
 * landing components.
 *
 * Each department exposes:
 *   key, label, code, icon, mission, head, responsibilities[]
 *
 * The "Operations" department's PAGE is intentionally NOT rendered from
 * here — CompanyManagerDashboard renders its existing Command Center
 * body when `activeDept === 'operations'`. Every other department lands
 * on `DepartmentLandingView` which presents a professional placeholder
 * scaffold (mission, head, responsibilities, quick actions).
 */
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Home, Crown, Cog, DollarSign, Users as UsersIcon, Cpu, Scale,
  TrendingUp, Megaphone, Headphones, Truck, ShieldCheck, GraduationCap,
  Lock, ChevronRight, Building2, MapPin, Phone, Mail,
} from 'lucide-react';
import ITAuthorizationsPanel from './ITAuthorizationsPanel';
import { DepartmentDetails } from './DepartmentDetails';
import UnifiedGeoRoleFilter from '../UnifiedGeoRoleFilter';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';

export const NECS_DEPARTMENTS = [
  {
    key: 'home',
    label: 'Home',
    code: null,
    icon: Home,
  },
  {
    key: 'executive',
    label: 'Executive Office',
    code: 'EXC',
    icon: Crown,
    mission: 'Set strategic direction, hold P&L accountability, and steward Noble Empire\'s long-term vision.',
    head: 'Chief Executive Officer',
    responsibilities: [
      'Company strategy, vision, and growth roadmap',
      'Board reporting and investor relations',
      'Cross-departmental policy approval',
      'M&A, partnerships, and capital allocation',
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    code: 'OPS',
    icon: Cog,
    mission: 'Run daily field service across every property: pickup, cleaning, fleet, and crew dispatch.',
    head: 'Director of Operations',
    responsibilities: [
      'Daily pickup execution and route coverage',
      'Fleet & vehicle dispatch + safety checks',
      'Crew scheduling, shift swaps, attendance',
      'Live incident response and field escalations',
      'Command Center — real-time operations overview',
    ],
  },
  {
    key: 'finance',
    label: 'Finance & Accounting',
    code: 'FIN',
    icon: DollarSign,
    mission: 'Protect financial integrity through accurate billing, payroll, and reporting.',
    head: 'Chief Financial Officer',
    responsibilities: [
      'Property invoicing & accounts receivable',
      'Payroll, expense reimbursements, vendor payments',
      'Monthly close, P&L, balance sheet',
      'Tax filings (federal · TX · DBA jurisdictions)',
      'Budget approvals and capital expenditures',
    ],
  },
  {
    key: 'hr',
    label: 'Human Resources',
    code: 'HRX',
    icon: UsersIcon,
    mission: 'Hire, develop, and protect the people who build Noble Empire.',
    head: 'Director of Human Resources',
    responsibilities: [
      'Recruiting, onboarding, and offboarding',
      'Benefits, payroll inputs, time-off',
      'Employee relations & disciplinary actions',
      'Compliance with federal / Texas labor law',
      'Cross-department access reviews',
    ],
  },
  {
    key: 'it',
    label: 'Information Technology',
    code: 'TEC',
    icon: Cpu,
    mission: 'Keep every system, device, and integration online — securely.',
    head: 'Director of Information Technology',
    responsibilities: [
      'Internal apps, web platform, mobile apps',
      'Identity & access management, MFA',
      'Endpoint security and device fleet',
      'Vendor integrations (Twilio · Resend · Stripe)',
      'Incident response and disaster recovery',
    ],
  },
  {
    key: 'legal',
    label: 'Legal & Compliance',
    code: 'LEG',
    icon: Scale,
    mission: 'Keep Noble Empire on the right side of every regulation, contract, and dispute.',
    head: 'General Counsel',
    responsibilities: [
      'Master service agreements with properties',
      'Vendor & subcontractor contracts',
      'OSHA / EPA / municipal compliance',
      'Litigation, claims, and risk transfer',
      'IT cross-access authorizations (with HR)',
    ],
  },
  {
    key: 'sales',
    label: 'Sales & Business Development',
    code: 'SLS',
    icon: TrendingUp,
    mission: 'Win new properties and grow revenue across the DFW metroplex and beyond.',
    head: 'VP of Sales',
    responsibilities: [
      'Outbound property prospecting & demos',
      'Proposal, pricing, and contract negotiation',
      'Customer expansion and renewals',
      'Pipeline reporting and forecasts',
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing & Communications',
    code: 'MKT',
    icon: Megaphone,
    mission: 'Tell the Noble Empire story — to properties, residents, recruits, and the public.',
    head: 'Director of Marketing',
    responsibilities: [
      'Brand standards, website, social media',
      'Property collateral, case studies, photography',
      'PR, press, and crisis communications',
      'Employer brand and recruiting content',
    ],
  },
  {
    key: 'customer-service',
    label: 'Customer Service',
    code: 'CSV',
    icon: Headphones,
    mission: 'Be the calm, professional voice every resident and property manager hears.',
    head: 'Customer Service Manager',
    responsibilities: [
      'Resident hotline, email, and in-app chat',
      'Property manager support and escalations',
      'Service-recovery credits and apologies',
      'CSAT tracking and root-cause feedback to Ops',
    ],
  },
  {
    key: 'procurement',
    label: 'Procurement & Supply Chain',
    code: 'PRC',
    icon: Truck,
    mission: 'Make sure trucks, bins, cleaning chemicals, and PPE are always ready.',
    head: 'Procurement Manager',
    responsibilities: [
      'Vendor sourcing and price negotiation',
      'Inventory of cans, liners, chemicals, uniforms',
      'Vehicle acquisition & disposal',
      'Sustainability & EPA-compliant sourcing',
    ],
  },
  {
    key: 'safety',
    label: 'Safety & Risk Management',
    code: 'SAF',
    icon: ShieldCheck,
    mission: 'Zero serious injuries. Every shift. Every vehicle. Every property.',
    head: 'Safety Director',
    responsibilities: [
      'Pre-/post-shift vehicle safety checklists',
      'Incident & near-miss reporting',
      'Workers comp claims management',
      'OSHA training records & audits',
      'Insurance liaison and loss prevention',
    ],
  },
  {
    key: 'training',
    label: 'Training & Development',
    code: 'TRN',
    icon: GraduationCap,
    mission: 'Make every Noble Empire employee better tomorrow than they were today.',
    head: 'Training & Development Manager',
    responsibilities: [
      'New-hire onboarding curriculum',
      'Annual safety & compliance certifications',
      'Leadership pipeline and promotions',
      'Skill assessments and re-certifications',
    ],
  },
];

export const DEPT_BY_KEY = NECS_DEPARTMENTS.reduce((acc, d) => {
  acc[d.key] = d;
  return acc;
}, {});

/* ─────────────────────────────────────────────────────────────────────── */
/* OperationsHeader — same banner style as every other department         */
/*   (mission + head + key responsibilities), shown at the top of the     */
/*   Operations / Command Center page. No personal name.                  */
/* ─────────────────────────────────────────────────────────────────────── */

export function OperationsHeader() {
  const terms = useAdminScopeTerms();
  const d = DEPT_BY_KEY.operations;
  const Icon = d.icon;
  return (
    <div className="mb-6" data-testid="operations-header">
      <div
        className="rounded-2xl p-6 mb-4 border flex items-start gap-4"
        style={{ background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)', borderColor: 'rgba(201,169,98,0.4)' }}
      >
        <div
          className="h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(201,169,98,0.18)', border: '1px solid #C9A962' }}
        >
          <Icon className="h-7 w-7" style={{ color: '#C9A962' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase mb-1" style={{ color: '#C9A962', letterSpacing: '0.22em' }}>
            Department · {d.code}
          </p>
          <h1
            className="text-2xl sm:text-3xl font-bold text-white"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
          >
            {d.label}
          </h1>
          <p className="text-sm text-zinc-300 mt-2 max-w-3xl leading-relaxed">{terms.text(d.mission)}</p>
        </div>
      </div>

      <DepartmentDetails dept={d} prefix="operations" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Home — personalized administration view                                 */
/*   • Top panel = the user's OWN department information page (mission,    */
/*     head, responsibilities). For Executive users this is the            */
/*     "Executive Information" panel.                                      */
/*   • Below      = Administration Command Index grid — only shown to      */
/*     users with multi-department access (Executive, HR, ...).            */
/* ─────────────────────────────────────────────────────────────────────── */

export function HomeAdministrationView({ onJump, canAccess, userDeptCode, user, boardFilter, minimal = false }) {
  const terms = useAdminScopeTerms();
  if (minimal) return <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-home-view"><p className="text-sm text-[#C9A962]" data-testid="admin-home-welcome">Welcome</p><h1 className="text-4xl sm:text-5xl font-bold mt-3 break-words" data-testid="home-welcome-strip-name">{user?.name}</h1></div>;
  // Find the user's own department metadata (by 3-letter code)
  const myDept = NECS_DEPARTMENTS.find((d) => d.code === userDeptCode) || DEPT_BY_KEY[user?.admin_department] || DEPT_BY_KEY.executive;

  // Count of departments the user can actually open (excluding "home" itself)
  const accessibleCount = NECS_DEPARTMENTS.filter(
    (d) => d.key !== 'home' && canAccess(d.key)
  ).length;
  const showOrgGrid = accessibleCount > 1; // org index only for multi-dept users

  const operatingDepartments = NECS_DEPARTMENTS.filter((d) => d.key !== 'home' && (!user?.admin_department || canAccess(d.key)));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-home-view">
      {/* Personalized welcome — full name, every admin, every Home page.
          Compact "executive letterhead" style: small uppercase eyebrow,
          italic serif name, no bombastic 4xl headline. */}
      {user?.name && (
        <div className="mb-5 flex items-start gap-3" data-testid="home-welcome-strip">
          <div
            aria-hidden
            className="mt-2 h-7 w-[3px] rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(180deg, #C9A962 0%, transparent 100%)' }}
          />
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.32em] font-semibold"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Welcome
            </p>
            <h2
              className="necs-dashboard-name text-lg sm:text-xl leading-tight"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
                fontWeight: 600,
                color: '#ffffff',
                letterSpacing: '0.005em',
              }}
              data-testid="home-welcome-strip-name"
            >
              {user.name}
            </h2>
          </div>
        </div>
      )}

      {/* ── User's own department information ───────────────────────────── */}
      {myDept && (() => {
        const Icon = myDept.icon;
        return (
          <>
            <div
              className="rounded-2xl p-6 sm:p-8 mb-6 border flex items-start gap-5"
              style={{
                background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)',
                borderColor: 'rgba(201,169,98,0.45)',
              }}
              data-testid="home-my-dept-banner"
            >
              <div
                className="h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(201,169,98,0.18)', border: '1px solid #C9A962' }}
              >
                <Icon className="h-7 w-7" style={{ color: '#C9A962' }} />
              </div>
              <div className="flex-1">
                <p
                  className="text-[10px] uppercase mb-1"
                  style={{ color: '#C9A962', letterSpacing: '0.22em' }}
                >
                  {myDept.key === 'executive'
                    ? 'Executive Information · Home'
                    : `${myDept.label} · Home`}
                </p>
                <h1
                  className="text-2xl sm:text-3xl font-bold text-white"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
                >
                  {myDept.label}
                </h1>
                <p className="text-sm text-zinc-300 mt-2 max-w-3xl leading-relaxed">
                  {terms.text(myDept.mission)}
                </p>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-8">
              <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-1" data-testid="home-my-dept-head">
                <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
                  Department Head
                </p>
                <p className="text-base font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {myDept.head}
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  {myDept.key === 'executive' ? 'Reports to: Board of Directors' : 'Reporting line: Executive Office'}
                </p>
                <div className="h-px w-full my-4" style={{ backgroundColor: 'rgba(201,169,98,0.25)' }} />
                <p className="text-[10px] uppercase tracking-widest mb-2 text-zinc-500" style={{ letterSpacing: '0.2em' }}>
                  Department Code
                </p>
                <Badge
                  className="text-xs"
                  style={{ backgroundColor: 'rgba(201,169,98,0.18)', color: '#C9A962', border: '1px solid rgba(201,169,98,0.4)' }}
                >
                  {myDept.code}
                </Badge>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2" data-testid="home-my-dept-resp">
                <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
                  Key Responsibilities
                </p>
                <ul className="space-y-2">
                  {(myDept.responsibilities || []).map((r, i) => (
                    <li key={`mydept-resp-${i}`} className="flex items-start gap-3 text-sm text-zinc-200">
                      <span
                        className="mt-1.5 inline-block rounded-full flex-shrink-0"
                        style={{ width: '6px', height: '6px', backgroundColor: '#C9A962' }}
                      />
                      <span>{terms.text(r)}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </>
        );
      })()}

      {/* ── Company banner + Administration Command Index ────────────────── */}
      {boardFilter && <UnifiedGeoRoleFilter filter={boardFilter} testIdPrefix="unified-filter-home" />}
      {showOrgGrid && (
        <>
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6 border"
            style={{
              background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)',
              borderColor: 'rgba(201,169,98,0.45)',
            }}
            data-testid="home-company-banner"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div
                  className="h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(201,169,98,0.18)', border: '1px solid #C9A962' }}
                >
                  <Building2 className="h-7 w-7" style={{ color: '#C9A962' }} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
                    Administration · Command Index
                  </p>
                  <h2
                    className="text-xl sm:text-2xl font-bold text-white"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
                  >
                    Noble Empire Cleaning Services
                  </h2>
                  <p className="text-sm text-zinc-300 mt-1">Dallas, TX · Valet Trash · Cleaning · Maintenance</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-zinc-200">
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4" style={{ color: '#C9A962' }} /> Dallas / Fort Worth</div>
                <div className="flex items-center gap-2"><Phone className="h-4 w-4" style={{ color: '#C9A962' }} /> +1 (888) 241-3077</div>
                <div className="flex items-center gap-2"><Mail className="h-4 w-4" style={{ color: '#C9A962' }} /> support@nobleempirecs.com</div>
              </div>
            </div>
          </div>

          <div className="flex items-baseline justify-between mb-4">
            <h2
              className="text-xl sm:text-2xl font-bold text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              Departments
            </h2>
            <span className="text-xs text-zinc-400">{accessibleCount} accessible</span>
          </div>
          <div className="h-px w-full mb-6" style={{ backgroundColor: 'rgba(201,169,98,0.35)' }} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-departments-grid">
            {operatingDepartments.map((d) => {
              const Icon = d.icon;
              const accessible = canAccess(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => onJump(d.key)}
                  className="text-left rounded-xl p-5 border transition group hover:-translate-y-0.5"
                  style={{
                    backgroundColor: 'rgba(20,20,20,0.65)',
                    borderColor: accessible ? 'rgba(201,169,98,0.35)' : 'rgba(255,255,255,0.08)',
                    cursor: accessible ? 'pointer' : 'not-allowed',
                    opacity: accessible ? 1 : 0.55,
                  }}
                  data-testid={`home-dept-card-${d.key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(201,169,98,0.15)', border: '1px solid rgba(201,169,98,0.4)' }}
                    >
                      <Icon className="h-5 w-5" style={{ color: '#C9A962' }} />
                    </div>
                    {accessible ? (
                      <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-[#C9A962] transition" />
                    ) : (
                      <Lock className="h-4 w-4 text-zinc-500" />
                    )}
                  </div>
                  <p
                    className="text-sm font-bold text-white mb-1"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}
                  >
                    {d.label}
                  </p>
                  {d.code && (
                    <Badge
                      className="mb-2 text-[10px]"
                      style={{ backgroundColor: 'rgba(201,169,98,0.18)', color: '#C9A962', border: '1px solid rgba(201,169,98,0.4)' }}
                    >
                      {d.code}
                    </Badge>
                  )}
                  <p className="text-xs text-zinc-400 leading-relaxed line-clamp-3">{terms.text(d.mission)}</p>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Department Landing — generic professional placeholder                   */
/* ─────────────────────────────────────────────────────────────────────── */

export function DepartmentLandingView({ deptKey, locked = false, viewerDeptCode = null }) {
  const d = DEPT_BY_KEY[deptKey];
  if (!d) {
    return (
      <div className="p-10 text-center text-zinc-400" data-testid={`dept-landing-missing-${deptKey}`}>
        Unknown department.
      </div>
    );
  }
  const Icon = d.icon;

  if (locked) {
    return (
      <div className="p-10 max-w-3xl mx-auto" data-testid={`dept-locked-${deptKey}`}>
        <Card className="bg-zinc-900 border-zinc-800 p-10 text-center">
          <Lock className="h-10 w-10 mx-auto mb-4" style={{ color: '#C9A962' }} />
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {d.label.toUpperCase()} — RESTRICTED
          </h2>
          <p className="text-sm text-zinc-400">
            You do not have clearance for this department. Contact your administrator or Human Resources to request access.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid={`dept-landing-${deptKey}`}>
      {/* Department header */}
      <div
        className="rounded-2xl p-6 mb-6 border flex items-start gap-4"
        style={{ background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)', borderColor: 'rgba(201,169,98,0.4)' }}
      >
        <div
          className="h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(201,169,98,0.18)', border: '1px solid #C9A962' }}
        >
          <Icon className="h-7 w-7" style={{ color: '#C9A962' }} />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: '#C9A962', letterSpacing: '0.22em' }}>
            Department · {d.code}
          </p>
          <h1
            className="text-2xl sm:text-3xl font-bold text-white"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
          >
            {d.label}
          </h1>
          <p className="text-sm text-zinc-300 mt-2 max-w-3xl leading-relaxed">{d.mission}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Head card */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-1" data-testid={`dept-head-card-${deptKey}`}>
          <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
            Department Head
          </p>
          <p className="text-base font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {d.head}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Reporting line: Executive Office</p>
          <div className="h-px w-full my-4" style={{ backgroundColor: 'rgba(201,169,98,0.25)' }} />
          <p className="text-[10px] uppercase tracking-widest mb-2 text-zinc-500" style={{ letterSpacing: '0.2em' }}>
            Headcount
          </p>
          <p className="text-2xl font-bold text-white">—</p>
          <p className="text-xs text-zinc-500">Live headcount wiring pending</p>
        </Card>

        {/* Responsibilities */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2" data-testid={`dept-responsibilities-${deptKey}`}>
          <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
            Key Responsibilities
          </p>
          <ul className="space-y-2">
            {(d.responsibilities || []).map((r, i) => (
              <li key={`${deptKey}-resp-${i}`} className="flex items-start gap-3 text-sm text-zinc-200">
                <span
                  className="mt-1.5 inline-block rounded-full flex-shrink-0"
                  style={{ width: '6px', height: '6px', backgroundColor: '#C9A962' }}
                />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid={`dept-comingsoon-${deptKey}`}>
        <p className="text-xs text-zinc-400">
          Live data, reports, and tooling for the <span className="text-white font-semibold">{d.label}</span> department
          will appear here as it is wired up. Use the navigation on the left to switch between divisions.
        </p>
      </Card>

      {/* IT Access Authorization — only inside HR (visible to HR officer)
          or Legal & Compliance (visible to Legal officer). Other admins
          viewing those pages don't see the dual-approval panel. */}
      {deptKey === 'hr' && viewerDeptCode === 'HRX' && <ITAuthorizationsPanel officerRole="hr" />}
      {deptKey === 'legal' && viewerDeptCode === 'LEG' && <ITAuthorizationsPanel officerRole="legal" />}
    </div>
  );
}
